import React, { useEffect, useState } from 'react';
import api from '../api';

// A DATE column comes back as 'YYYY-MM-DD'. Build the Date from its parts so
// the browser's timezone can never shift it onto the day before.
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return String(d);
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Today in the coach's own timezone, as YYYY-MM-DD. Never toISOString() —
// that converts to UTC and can hand back yesterday.
const todayInput = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const describe = (s) => {
  if (s.session_type === 'away_game') return `Away @ ${s.venue_name || 'venue not set'}`;
  // A home match and a training session are both named after the team, so the
  // kind has to be said or they read identically on the list.
  if (s.session_type === 'home_match') return `Home match - ${s.session_label || 'Session'}`;
  return s.session_label || 'Session';
};

const CoachHome = () => {
  const [venues, setVenues] = useState([]);
  // The mileage rate comes from the server, never from a number typed into
  // this file. Until it has loaded it stays null and the estimate below is
  // simply not shown — a wrong figure on screen is worse than no figure.
  const [mileageRate, setMileageRate] = useState(null);
  const [teamGroups, setTeamGroups] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [bills, setBills] = useState([]);

  const [sessionType, setSessionType] = useState('session');
  const [sessionDate, setSessionDate] = useState(todayInput());
  const [sessionLabel, setSessionLabel] = useState('');
  const [venueId, setVenueId] = useState('');
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [v, r, t, s, b] = await Promise.all([
      api.get('/api/venues'),
      api.get('/api/venues/mileage-rate'),
      api.get('/api/teams'),
      api.get('/api/submissions'),
      api.get('/api/bills')
    ]);
    // The coach never types mileage: away mileage comes from this list only,
    // and the home court is not an away trip.
    setVenues(v.data.filter(x => x.active !== false && !x.is_home_court));
    const rate = Number(r.data.mileage_rate_per_mile);
    setMileageRate(Number.isFinite(rate) ? rate : null);
    // The club's own team list, grouped as the club groups it. The names come
    // from the server, never from a list typed into this file.
    setTeamGroups(t.data.groups || []);
    setSubmissions(s.data);
    setBills(b.data);
  };

  useEffect(() => { load(); }, []);

  const selectedVenue = venues.find(v => String(v.id) === String(venueId));
  const hasTeams = teamGroups.some(g => g.teams && g.teams.length);

  const reset = () => {
    setSessionLabel(''); setVenueId(''); setHours(''); setNotes('');
    setSessionDate(todayInput());
  };

  const postSubmission = (confirmDuplicate) => api.post('/api/submissions', {
    session_date: sessionDate,
    session_type: sessionType,
    session_label: sessionType !== 'away_game' ? sessionLabel : null,
    venue_id: sessionType === 'away_game' ? Number(venueId) : null,
    hours: Number(hours),
    notes,
    ...(confirmDuplicate ? { confirm_duplicate: true } : {})
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (submitting) return; // guards a double tap on a phone
    setSubmitting(true);
    try {
      const res = await postSubmission(false);
      setSuccess(res.data.warning || 'Session logged.');
      reset();
      await load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.duplicate) {
        const list = (data.existing || [])
          .map(x => `${describe(x)} — ${x.hours} ${Number(x.hours) === 1 ? 'hour' : 'hours'}`)
          .join('\n');
        const ok = window.confirm(
          `You have already logged this on ${fmtDate(sessionDate)}:\n\n${list}\n\nLog this one as well?`
        );
        if (!ok) { setSubmitting(false); return; }
        try {
          const res = await postSubmission(true);
          setSuccess(res.data.warning || 'Second session logged for that date.');
          reset();
          await load();
        } catch (err2) {
          setError(err2.response?.data?.error || 'Could not submit');
        }
      } else {
        setError(data?.error || 'Could not submit');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // A session the club has sent back, being put right.
  //
  // Only one at a time, and only ever one of their own — the server checks
  // that too. Saving it sends it straight back in as submitted, which is why
  // there is no separate "resubmit" button to forget to press.
  const [fixing, setFixing] = useState(null);   // { id, hours }
  const [fixError, setFixError] = useState('');

  const saveFix = async (sub) => {
    setFixError('');
    try {
      await api.patch(`/api/submissions/${sub.id}`, { hours: fixing.hours });
      setFixing(null);
      load();
    } catch (err) {
      setFixError(err.response?.data?.error || 'That could not be saved.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this submission?')) return;
    await api.delete(`/api/submissions/${id}`);
    load();
  };

  const canSubmit = hours && sessionDate &&
    (sessionType !== 'away_game' ? sessionLabel.trim() : venueId);

  return (
    <>
      <h1>My hours</h1>
      <div className="card">
        <h2>Log a session</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}
        <form onSubmit={submit}>
          <div className="form-row">
            <label>What was it?</label>
            <div className="tabs">
              <button type="button" className={sessionType === 'session' ? 'active' : ''}
                      onClick={() => { setSessionType('session'); setError(''); }}>
                Training session
              </button>
              {/* New in November 2026. The portal could not tell a home match
                  from a training session before, because both are at the home
                  court against no venue, which is exactly why a match fee
                  could not be paid on one. */}
              <button type="button" className={sessionType === 'home_match' ? 'active' : ''}
                      onClick={() => { setSessionType('home_match'); setError(''); }}>
                Home match
              </button>
              <button type="button" className={sessionType === 'away_game' ? 'active' : ''}
                      onClick={() => { setSessionType('away_game'); setError(''); }}>
                Away game
              </button>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-row">
              <label>Date coached</label>
              <input type="date" value={sessionDate} max={todayInput()}
                     onChange={e => setSessionDate(e.target.value)} required />
            </div>

            {sessionType !== 'away_game' ? (
              <div className="form-row">
                <label>Which session?</label>
                {hasTeams ? (
                  <select value={sessionLabel} onChange={e => setSessionLabel(e.target.value)} required>
                    <option value="">Select…</option>
                    {teamGroups.map(g => (
                      <optgroup key={g.name} label={g.name}>
                        {g.teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                ) : (
                  // Only reachable if no teams have been set up yet. A coach can
                  // still log their hours rather than being stuck.
                  <input type="text" maxLength={120} value={sessionLabel}
                         placeholder="e.g. U14 Performance"
                         onChange={e => setSessionLabel(e.target.value)} required />
                )}
              </div>
            ) : (
              <div className="form-row">
                <label>Venue travelled to</label>
                <select value={venueId} onChange={e => setVenueId(e.target.value)} required>
                  <option value="">Select…</option>
                  {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}

            <div className="form-row">
              <label>Hours coached</label>
              <input type="number" step="0.25" min="0.25" value={hours}
                     onChange={e => setHours(e.target.value)} required />
            </div>
          </div>

          {sessionType !== 'away_game' && !hasTeams && (
            <p className="muted">No sessions have been set up yet, so type the name for now. Ask an admin to add the club's teams.</p>
          )}

          {sessionType === 'away_game' && venues.length === 0 && (
            <p className="error-msg">No away venues have been set up yet. Ask an admin to add one.</p>
          )}

          {sessionType === 'away_game' && selectedVenue && (
            <p className="muted">
              Away game — mileage auto-fills at {selectedVenue.round_trip_miles ?? '?'} miles round trip
              from the home court
              {selectedVenue.round_trip_miles && mileageRate !== null
                ? ` (approx. £${(selectedVenue.round_trip_miles * mileageRate).toFixed(2)})`
                : ''}.
              No need to enter it yourself.
              {selectedVenue.round_trip_miles == null && ' Mileage is not set for this venue yet, so it will save as 0 miles until an admin adds it.'}
            </p>
          )}

          <div className="form-row">
            <label>Notes (optional)</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button className="btn" type="submit" disabled={submitting || !canSubmit}>
            {submitting ? 'Submitting…' : 'Log session'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>My submissions</h2>
        {fixError && <div className="error-msg prewrap">{fixError}</div>}
        {submissions.length === 0 ? <p className="muted">Nothing submitted yet.</p> : (
          <table>
            <thead>
              <tr><th>Date</th><th>Session</th><th>Hours</th><th>Mileage</th><th>Total</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {submissions.flatMap(s => ([
                <tr key={s.id} className={s.status === 'declined' ? 'needs-attention' : undefined}>
                  <td>{fmtDate(s.session_date)}</td>
                  <td>{describe(s)}</td>
                  <td>
                    {fixing && fixing.id === s.id ? (
                      <input
                        className="hours-input"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="24"
                        value={fixing.hours}
                        autoFocus
                        onChange={e => setFixing({ ...fixing, hours: e.target.value })}
                      />
                    ) : s.hours}
                    {/* If the club changed the figure, the coach being paid on
                        it should not have to spot that for themselves. */}
                    {s.amended_at && (
                      <div className="muted amended-note">
                        was {Number(s.amended_from_hours)}, changed by {s.amended_by_name}
                      </div>
                    )}
                  </td>
                  <td>{s.miles > 0 ? `${s.miles} mi / £${Number(s.mileage_amount).toFixed(2)}` : '—'}</td>
                  <td>£{Number(s.line_total).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${s.status === 'declined' ? 'submitted' : s.status}`}>
                      {s.status === 'declined' ? 'Sent back' : s.status}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      {fixing && fixing.id === s.id ? (
                        <>
                          <button className="btn small" onClick={() => saveFix(s)}>Save and send again</button>
                          <button className="btn small secondary" onClick={() => setFixing(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {s.status === 'declined' && (
                            <button className="btn small" onClick={() => setFixing({ id: s.id, hours: s.hours })}>
                              Put it right
                            </button>
                          )}
                          {s.status === 'submitted' && (
                            <button className="btn small secondary" onClick={() => remove(s.id)}>Remove</button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>,
                s.status === 'declined' && s.declined_reason ? (
                  <tr key={`${s.id}-why`} className="sendback-row">
                    <td colSpan={7}>
                      <strong>The club sent this back:</strong> {s.declined_reason}
                    </td>
                  </tr>
                ) : null
              ]))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>My bills</h2>
        {bills.length === 0 ? <p className="muted">No bills raised yet.</p> : (
          <table>
            <thead><tr><th>Period</th><th>Hours</th><th>Mileage</th><th>Total</th><th>Status</th></tr></thead>
            <tbody>
              {bills.map(b => (
                <tr key={b.id}>
                  <td>{b.period_label}</td>
                  <td>£{Number(b.total_hours_amount).toFixed(2)}</td>
                  <td>£{Number(b.total_mileage_amount).toFixed(2)}</td>
                  <td>£{Number(b.total_amount).toFixed(2)}</td>
                  <td><span className={`badge ${b.status}`}>{b.status.replace(/_/g, ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default CoachHome;
